<template>
    <div id="app">
        <transition name="fade" mode="out-in">
            <router-view />
        </transition>
        <portal-target multiple name="default" />
        <portal-target multiple name="modal" id="modal" />
        <portal-target multiple name="dropdown" id="dropdown" />
        <portal-target multiple name="popover" id="popover" />
        <portal-target multiple name="drawer" id="drawer" />
        <portal-target multiple name="datePicker" id="datePicker" />
        <portal-target multiple name="sidePanel" id="sidePanel" />
        <portal-target multiple name="cookieConsent" id="cookie-consent" />
        <portal-target multiple name="animation" id="animation" />
        <portal-target multiple name="overlay" id="overlay" />

        <!-- Maintenance Notification - visible on all pages -->
        <MaintenanceNotification
            :notification="maintenanceNotification"
            :isOverviewPage="isOverviewPage"
            @maintenance-finished="handleMaintenanceFinished"
        />

        <!-- Notification Banner - visible on all pages -->
        <NotificationBanner :banner="activeBanner" />

        <PWAInstallPrompt ref="pwaPrompt" />
        <FeatureAnnouncementManager v-if="isAuthenticated" />
        <MaintenanceHandler />
        <CommandPalette />
        <UpdateOverlay :show="showUpdateOverlay" />
    </div>
</template>
<script>
import axios from 'axios';
import 'remixicon/fonts/remixicon.css';
import MaintenanceHandler from '@/components/MaintenanceHandler.vue';
import MaintenanceNotification from '@/components/MaintenanceNotification.vue';
import NotificationBanner from '@/components/NotificationBanner.vue';
import PWAInstallPrompt from '@/components/PWAInstallPrompt.vue';
import FeatureAnnouncementManager from '@/components/FeatureAnnouncementManager.vue';
import CommandPalette from '@/components/CommandPalette.vue';
import UpdateOverlay from '@/components/UpdateOverlay.vue';
import { maintenanceService } from '@/services/axiosConfig';
import metaPixelService from '@/services/metaPixel';
import pwaService from '@/services/pwaService';

export default {
    name: 'app',
    components: {
        MaintenanceHandler,
        MaintenanceNotification,
        NotificationBanner,
        PWAInstallPrompt,
        FeatureAnnouncementManager,
        CommandPalette,
        UpdateOverlay,
    },
    data() {
        return {
            windowWidth: window.innerWidth,
            pwaPromptShown: false,
            showUpdateOverlay: false,
        };
    },
    computed: {
        isOverviewPage() {
            return this.$route.name === 'overview';
        },
        maintenanceNotification() {
            return maintenanceService.getMaintenanceStatus();
        },
        isAuthenticated() {
            return !!localStorage.getItem('authToken');
        },
        activeBanner() {
            return this.$store.getters['notificationBanners/activeBanner'];
        },
    },
    methods: {
        handleMaintenanceFinished() {
            maintenanceService.checkServerMaintenanceStatus();
            this.$toasted.show('Wartung ist beendet. System ist wieder online.', {
                type: 'success',
                duration: 5000,
            });
        },

        handleCookieConsent(preferences) {
            metaPixelService.updateConsent(preferences);
        },

        handleGlobalCookieConsent(event) {
            const preferences = event.detail;
            this.handleCookieConsent(preferences);
        },

        checkAndShowPWAPrompt() {
            const isAuthenticated = !!localStorage.getItem('authToken');
            const isDashboardRoute =
                this.$route.name === 'overview' || this.$route.path.startsWith('/dashboard');

            if (!isAuthenticated || !isDashboardRoute || !pwaService.shouldShowPrompt()) {
                return;
            }

            const tokenTimestamp = localStorage.getItem('authTokenTimestamp');
            const now = Date.now();
            const isRecentLogin = !tokenTimestamp || now - parseInt(tokenTimestamp) < 60000;

            if (isRecentLogin) {
                if (this.pwaPromptShown) return;
                this.pwaPromptShown = true;

                setTimeout(() => {
                    if (this.$refs.pwaPrompt) {
                        this.$refs.pwaPrompt.showPrompt();
                    }
                }, 2000);
            }
        },
    },
    mounted() {
        this.$router.beforeEach((to, from, next) => {
            maintenanceService.checkMaintenanceStatusOnRouteChange();
            next();
        });

        window.addEventListener('cookie-consent-updated', this.handleGlobalCookieConsent);

        if (this.isAuthenticated) {
            this.$store.dispatch('notificationBanners/loadDismissedBanners');
            // TODO: Uncomment when API endpoint is available
            // this.$store.dispatch('notificationBanners/fetchBanners');
        }

        this.checkAndShowPWAPrompt();

        this.$router.afterEach((to, from) => {
            const isDashboardRoute = to.name === 'overview' || to.path.startsWith('/dashboard');
            if (isDashboardRoute) {
                this.checkAndShowPWAPrompt();
            }
        });

        const pendingPath = sessionStorage.getItem('pendingReloadPath');
        if (pendingPath && pendingPath !== this.$route.fullPath) {
            sessionStorage.removeItem('pendingReloadPath');
            this.$router.replace(pendingPath).catch(() => {});
        }

        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.getRegistrations().then((registrations) => {
                registrations.forEach((registration) => {
                    registration.addEventListener('updatefound', () => {
                        const newWorker = registration.installing;
                        newWorker.addEventListener('statechange', () => {
                            if (
                                newWorker.state === 'installed' &&
                                navigator.serviceWorker.controller
                            ) {
                                this.showUpdateOverlay = true;
                            }
                        });
                    });
                });
            });
        }
    },

    beforeDestroy() {
        window.removeEventListener('cookie-consent-updated', this.handleGlobalCookieConsent);
    },
};
</script>
<style lang="scss">
@import './styles/styles.scss';
@font-face {
    font-display: swap;
    font-family: 'Open Sans';
    font-style: normal;
    font-weight: 300;
    src: url('./assets/fonts/open-sans-v40-latin-300.woff2') format('woff2');
}

@font-face {
    font-display: swap;
    font-family: 'Open Sans';
    font-style: normal;
    font-weight: 400;
    src: url('./assets/fonts/open-sans-v40-latin-regular.woff2') format('woff2');
}

@font-face {
    font-display: swap;
    font-family: 'Open Sans';
    font-style: normal;
    font-weight: 700;
    src: url('./assets/fonts/open-sans-v40-latin-700.woff2') format('woff2');
}

#app {
    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.2s ease-out;
        will-change: opacity;
        backface-visibility: hidden;
        -webkit-backface-visibility: hidden;
    }

    .fade-enter-active {
        transition-delay: 0.05s;
    }

    .fade-enter,
    .fade-leave-to {
        opacity: 0;
    }

    .fade-leave-to {
        position: absolute;
        width: 100%;
    }
}

body::-webkit-scrollbar {
    background-color: var(--color-white);
    width: 10px;
    height: 10px;
}

body::-webkit-scrollbar-track {
    background-color: var(--color-white);
}

body::-webkit-scrollbar-thumb {
    background-color: var(--color-blue-dark);
    border-radius: 16px;
    width: 10px;
    border: 4px solid var(--color-white);
}

body::-webkit-scrollbar-button {
    display: none;
}
.toasted.toasted-primary.info {
    background: var(--color-blue-darker) !important;
    color: white !important;
    border-radius: 8px !important;
    box-shadow: 0 4px 12px rgba(52, 152, 219, 0.3) !important;
}

.toasted.toasted-primary.info .action {
    background-color: rgba(255, 255, 255, 0.2) !important;
    color: white !important;
    border-radius: 4px !important;
    margin-left: 8px !important;
    padding: 4px 12px !important;
    transition: background-color 0.2s ease !important;
}

.toasted.toasted-primary.info .action:hover {
    background-color: rgba(255, 255, 255, 0.3) !important;
}

.toasted.toasted-primary.info .action:first-child {
    margin-right: 4px !important;
    margin-left: 12px !important;
    background-color: var(--color-green-dark) !important;
}

.toasted.toasted-primary.info .action:first-child:hover {
    background-color: var(--color-green-dark) !important;
}
</style>
